import React from 'react';
import { useHistory } from 'react-router-dom';

import { EeAutocomplete } from 'components/EeAutocomplete/EeAutocomplete';
import {
  Fund,
  FundAggregation,
  Tag,
  TagsTagTypeChoices,
} from 'libs/api/invest/generated/schema';

export function FundSelector(props: {
  funds: Fund[] | undefined;
  fundAggregation: FundAggregation;
  fundIsLoading: boolean;
  reloadFund: (
    aggregationId: string,
    aggregationType: TagsTagTypeChoices | undefined,
    currencyCode?: string,
  ) => Promise<void>;
}) {
  const history = useHistory();
  const aggregations = (() => {
    let result = [] as {
      id: string;
      name: string;
      verboseType: string;
      isNested?: boolean;
    }[];
    props.funds?.forEach((f) => {
      result.push({ ...f, verboseType: 'Funds' });
      if (f.subfunds.length) {
        result = result.concat(
          f.subfunds.map((s: any) => ({
            ...s,
            verboseType: 'Funds',
            isNested: true,
          })),
        );
      }
    });
    return result;
  })().concat(
    getUniqueItems(
      (props.funds || [])
        .map((f) => f.divisions)
        .flat()
        .map((d) => ({ ...d, verboseType: 'Divisions' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    ),
    getUniqueItems(
      (props.funds || [])
        .map((f) => f.groups)
        .flat()
        .map((g) => ({ ...g, verboseType: 'Fund Groups' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    ),
  );

  // Create a list of currencies for selected fund and its associated companies.
  const fundCurrency = (props.funds || []).find((fund) =>
    [
      fund.id,
      ...fund.groups.map((g) => g.id),
      ...fund.divisions.map((d) => d.id),
      ...fund.subfunds.map((s) => s.id),
    ].includes(props.fundAggregation.aggregationId),
  );
  const companyRawCurrency =
    fundCurrency?.fundInvestments.map((a) => a.company.rawCurrency) || [];
  const currencies = [fundCurrency?.rawCurrency, ...companyRawCurrency].filter(
    (item, pos, self) => item && self.indexOf(item) === pos,
  );
  // const currencies = props
  //   .funds
  //   .map(f => f.rawCurrency)
  //   .filter((v, i, a) => a.indexOf(v) === i);  // unique values

  const [selectedAggregation, setSelectedAggregation] = React.useState<{
    name: string;
    id: string | null;
    type?: TagsTagTypeChoices;
  }>(
    aggregations.find((a) => a.id === props.fundAggregation.aggregationId) ||
      aggregations[0],
  );

  React.useEffect(() => {
    setSelectedAggregation(
      aggregations.find((a) => a.id === props.fundAggregation.aggregationId) ||
        aggregations[0],
    );

    const queryParams = new URLSearchParams(window.location.search);
    props.fundAggregation.aggregationType
      ? queryParams.set('aggrType', props.fundAggregation.aggregationType)
      : queryParams.delete('aggrType');
    props.fundAggregation.currency
      ? queryParams.set('currency', props.fundAggregation.currency)
      : queryParams.delete('currency');

    history.replace(
      `/fund/${props.fundAggregation.aggregationId}?${queryParams.toString()}`,
    );
  }, [props.fundAggregation]);

  function getUniqueItems<T extends Tag>(items: T[]) {
    return Array.from(new Map(items.map((i) => [i.id, i])).values());
  }

  return (
    <>
      <EeAutocomplete
        disabled={props.fundIsLoading}
        value={aggregations.find((a) => a.id === selectedAggregation?.id)}
        onChange={(e, option: Tag) => {
          setSelectedAggregation({ ...option, type: option.type || undefined });
          props.reloadFund(option.id, option.type || undefined);
        }}
        options={aggregations}
        groupBy={(option: Tag & { verboseType: string }) => option.verboseType}
        getOptionLabel={(option: Tag) => option.name || ''}
        getOptionSelected={(option: Tag) =>
          option.id === props.fundAggregation.aggregationId
        }
      />
      <EeAutocomplete
        disabled={props.fundIsLoading}
        value={props.fundAggregation.currency}
        onChange={(e, option: string) => {
          props.reloadFund(
            props.fundAggregation.aggregationId,
            props.fundAggregation.aggregationType || undefined,
            option,
          );
        }}
        options={currencies}
        width={110}
      />
    </>
  );
}
